import React, { useState } from 'react';
import { useGetList, useGetMany, useRefresh } from 'react-admin';
import {
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
	Link as MuiLink,
	Box,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { Link as RouterLink } from 'react-router-dom';

import { DEAL_TYPES, LEGACY_DEAL_TYPE_LABELS } from '../../Services/partners';
import { EditAttributionTermsDialog } from './EditAttributionTermsDialog';

// Clients attribués à un partenaire, vus depuis l'admin Quanti.
//
// Pendant exact de l'onglet « Partenaire » d'un billing account : là-bas on part du
// client et on cherche qui le touche, ici on part du partenaire et on cherche ce qu'il
// détient. Les deux se répondent, et les deux noms sont cliquables — sans quoi il faut
// retenir un nom et le rechercher dans l'autre liste.
//
// Ressource react-admin `attributions` → api-partners /v1/admin. Le filtre est en
// SNAKE_CASE (`partner_id`) : côté API la clé de filtre est concaténée à un nom de
// colonne SQL, un `partnerId` en camelCase serait rejeté et renverrait TOUTES les
// attributions — donc les clients des autres partenaires.

type Attribution = {
	id: number;
	billingAccountId: string;
	commissionType?: string;
	rate?: number;
	status: string;
	startAt?: string;
	commissionEndAt?: string;
	opportunity?: { id: number; companyName: string };
};

// Libellé du type d'affaire. Les valeurs vivantes viennent de DEAL_TYPES (partagé avec le
// dialogue d'association et la grille de commission) ; les anciennes ne sont là que pour
// rester lisibles si une ligne a échappé à la migration.
const dealTypeLabel = (slug?: string) =>
	DEAL_TYPES.find((t) => t.id === slug)?.name || (slug ? LEGACY_DEAL_TYPE_LABELS[slug] : undefined) || slug || '—';

const fmtDate = (d?: string) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');

// #region PartnerClients
export const PartnerClients: React.FC<{ partnerId: string }> = ({ partnerId }) => {
	const { data, isLoading } = useGetList<any>(
		'attributions',
		{ pagination: { page: 1, perPage: 100 }, sort: { field: 'created_at', order: 'DESC' }, filter: { partner_id: partnerId } },
		{ enabled: !!partnerId }
	);

	const rows = (data || []) as Attribution[];
	const refresh = useRefresh();
	const [editing, setEditing] = useState<Attribution | null>(null);

	// Les noms des BA sont résolus en UN seul appel groupé plutôt qu'un par ligne.
	// L'attribution ne porte que l'id du client : afficher un UUID reviendrait à ne pas
	// lister les clients du tout.
	const baIds = rows.map((r) => r.billingAccountId).filter(Boolean);
	const { data: accounts } = useGetMany<any>('billing-accounts', { ids: baIds }, { enabled: baIds.length > 0 });
	const nameById = new Map<string, string>((accounts || []).map((a: any) => [String(a.id), a.name as string]));

	return (
		<Card sx={{ mb: 2 }}>
			<CardContent>
				<Typography variant="h6" gutterBottom>
					Clients attribués
				</Typography>

				{isLoading ? (
					<Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
						<CircularProgress size={24} />
					</Box>
				) : rows.length === 0 ? (
					<Typography variant="body2" color="text.secondary">
						Aucun client attribué à ce partenaire.
					</Typography>
				) : (
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Client</TableCell>
								<TableCell>Type</TableCell>
								<TableCell align="right">Taux</TableCell>
								<TableCell>Statut</TableCell>
								<TableCell>Début de commissionnement</TableCell>
								<TableCell>Fin de commissionnement</TableCell>
								<TableCell />
							</TableRow>
						</TableHead>
						<TableBody>
							{rows.map((a) => (
								<TableRow key={a.id}>
									<TableCell>
										<MuiLink
											component={RouterLink}
											to={`/billing-accounts/${a.billingAccountId}`}
											variant="body2"
											underline="hover"
											sx={{ fontWeight: 600 }}
										>
											{nameById.get(a.billingAccountId) || a.billingAccountId}
										</MuiLink>
										{a.opportunity?.companyName && (
											<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
												Opportunité : {a.opportunity.companyName}
											</Typography>
										)}
									</TableCell>
									<TableCell>{dealTypeLabel(a.commissionType)}</TableCell>
									<TableCell align="right">{a.rate ? `${a.rate} %` : '—'}</TableCell>
									<TableCell>
										{/* Le statut est affiché parce qu'une attribution clôturée reste dans
										    la liste : elle explique l'historique des commissions, et sans le
										    statut on la lirait comme un client encore actif. */}
										<Chip
											label={a.status === 'active' ? 'Active' : 'Clôturée'}
											size="small"
											color={a.status === 'active' ? 'success' : 'default'}
										/>
									</TableCell>
									<TableCell>{fmtDate(a.startAt)}</TableCell>
									{/* « — » = pas de fin. Ce n'est plus le défaut d'un type (une affaire
									    `partner` est bornée à 2 ans depuis le 28/08/2026) mais un choix
									    explicite, posé via « Termes ». */}
									<TableCell>{fmtDate(a.commissionEndAt)}</TableCell>
									<TableCell align="right">
										<Button size="small" startIcon={<EditIcon />} onClick={() => setEditing(a)}>
											Termes
										</Button>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}

				<EditAttributionTermsDialog
					attribution={editing}
					clientName={editing ? nameById.get(editing.billingAccountId) || editing.billingAccountId : undefined}
					onClose={() => setEditing(null)}
					onSaved={refresh}
				/>
			</CardContent>
		</Card>
	);
};
// #endregion
